import React, { useState } from 'react';
import {  Phone, Mail, MapPin, CheckCircle, Shield, Star, Clock, AlertTriangle, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';
import reviewData from '../reviews.json';
import { SERVICE_AREAS } from '../serviceAreas';

const ContactPage = () => {
  const { QuoteModal } = useQuoteModal();
  usePageMeta(
    'Contact Us | Free Tree Service Quotes | One Choice Tree Service',
    'Get a free tree service quote in Maryland. Call (443) 208-0747 or send a request online — 24/7 emergency response for storm damage and fallen trees.'
  );
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error' | 'validation'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    details: ''
  });

  const handleInputChange = (e) => {
    if (submitStatus === 'validation') setSubmitStatus(null);
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setSubmitStatus('validation');
      return;
    }
    setSubmitStatus(null);

    // Set loading state
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton.innerHTML;
    submitButton.innerHTML = `
      <svg class="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Sending...
    `;
    submitButton.disabled = true;

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '80c96390-50a6-4c0a-b38a-a671e4b7f92b',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          service: formData.service,
          message: formData.details,
          subject: 'New Tree Service Contact Form Submission',
          from_name: `${formData.firstName} ${formData.lastName}`,
          // Additional fields for better organization
          form_type: 'Contact Page Form',
          service_requested: formData.service || 'Not specified',
          property_address: formData.address || 'Not provided'
        })
      });

      if (response.ok) {
        // Google Ads conversion tracking (Website lead)
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-18371423166/5g94CJ_bodwcEL7XlrhE' });
        }

        // Success
        setSubmitStatus('success');

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          details: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      // Reset button
      submitButton.innerHTML = originalText;
      submitButton.disabled = false;
    }
  };

  // Mirrors the Google Ads campaign targeting (Howard, Anne Arundel,
  // Baltimore, Carroll counties) so the landing page matches the ad geography.
  const serviceAreas = [
    'Anne Arundel County', 'Howard County', 'Baltimore County',
    'Carroll County', 'And surrounding communities'
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['105 Wallace Ave', 'Brooklyn, MD 21225'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['(443) 208-0747', '24/7 Emergency Line'],
      color: 'from-green-500 to-green-600',
      action: 'tel:+14432080747'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['onechoicetreeservice.md@gmail.com', 'Free quotes & questions'],
      color: 'from-purple-500 to-purple-600',
      action: 'mailto:onechoicetreeservice.md@gmail.com'
    },
    {
      icon: Clock,
      title: 'Hours',
      details: ['Mon-Fri: 7AM - 6PM', 'Sat: 8AM - 4PM', 'Sun: Emergency Only'],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const faqItems = [
    {
      question: "Who do you serve?",
      answer: "We serve residential and commercial customers throughout Anne Arundel, Howard, Baltimore, and Carroll counties. Our services are perfect for homeowners, business owners, property managers, and municipalities looking for professional tree care."
    },
    {
      question: "What makes One Choice Tree Service different?",
      answer: "As a newly established local company, we bring fresh energy and dedication to every project. We're fully licensed and insured, offer competitive pricing, and prioritize both safety and customer satisfaction. Our 'one choice' philosophy means you get quality, safety, and value all in one package."
    },
    {
      question: "Where do you operate?",
      answer: "We're based in Brooklyn, MD and serve Anne Arundel, Howard, Baltimore, and Carroll counties - including Glen Burnie, Pasadena, Severn, Ellicott City, Highland, Clarksville, Towson, Westminster, and surrounding communities. Contact us to confirm service availability in your specific location."
    },
    {
      question: "How do I get started?",
      answer: "Simply contact us for a free consultation and quote. We'll assess your tree care needs, provide a detailed estimate, and schedule the work at your convenience. For emergencies, we offer 24/7 response services."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes! One Choice Tree Service LLC is a licensed Maryland business in good standing (Est. 2025) and carries comprehensive liability insurance to protect you and your property."
    },
    {
      question: "Do you offer emergency services?",
      answer: "Absolutely. We provide 24/7 emergency response for storm damage, fallen trees, and urgent safety hazards. Contact us immediately if you have a tree emergency."
    },
    {
      question: "What happens to the debris after tree removal?",
      answer: "We handle complete cleanup and debris removal as part of our service. Wood chips can often be left for your use as mulch, or we can remove everything - your choice!"
    },
    {
      question: "Do you provide free estimates?",
      answer: "Yes, we provide free, no-obligation quotes for all our services. We'll visit your property, assess your needs, and provide a detailed written estimate."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Header currentPage="contact" />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="w-4 h-4 mr-2" />
            Ready to Help 24/7
          </div>

          <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Get Your Free
            <br />
            <span className="text-green-600">Tree Quote Today</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Fast response. Fair pricing. Professional results. Let's solve your tree problem.
          </p>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-4 bg-amber-50 border-y border-amber-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-center space-x-3 text-amber-900">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <a href="tel:+14432080747" className="font-semibold underline decoration-amber-300 underline-offset-2 hover:text-amber-700 hover:decoration-amber-500 transition-colors">
              Tree Emergency? Call (443) 208-0747 - 24/7 Response
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="quote-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Request Your Free Quote
            </h2>
            <p className="text-lg text-gray-600">
              Tell us about your project and we'll get back to you within 24 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-6">
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Your first name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Your last name"
                    required
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="(443) 208-0747"
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Where is the tree work needed?"
                />
              </div>

              {/* Service Needed */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Needed
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select a service</option>
                  <option value="tree-removal">Tree Removal</option>
                  <option value="tree-trimming">Tree Trimming/Pruning</option>
                  <option value="tree-health">Tree Health Assessment</option>
                  <option value="emergency">Emergency Service</option>
                  <option value="commercial">Commercial Service</option>
                  <option value="other">Other/Multiple Services</option>
                </select>
              </div>

              {/* Project Details */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  name="details"
                  value={formData.details}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Tell us about your tree situation. What type of tree? How big? Any concerns or special requirements?"
                />
              </div>

              {/* Inline form status */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-green-900">What happens next?</p>
                    <p className="text-sm text-green-800">We'll contact you within 24 hours with your free quote. Need us sooner? Call <a href="tel:+14432080747" className="font-semibold underline">(443) 208-0747</a>.</p>
                  </div>
                </div>
              )}
              {submitStatus === 'validation' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                  Please fill in all required fields (Name, Email, Phone).
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                  We couldn't send your request. Please try again, or call us at <a href="tel:+14432080747" className="font-semibold underline">(443) 208-0747</a>.
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 px-8 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
                <span>Send My Free Quote Request</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-lg text-gray-600">
              Multiple ways to reach us. Choose what works best for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center ${
                  info.action ? 'cursor-pointer hover:scale-105' : ''
                }`}
                onClick={() => info.action && window.open(info.action, '_self')}
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${info.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <info.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{info.title}</h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className={`text-gray-600 ${idx === 0 ? 'font-medium break-words' : 'text-sm'}`}>
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            We Serve These Areas
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, index) => (
              <span
                key={index}
                className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-medium"
              >
                {area}
              </span>
            ))}
          </div>
          <p className="text-gray-600 mt-8 mb-4">Towns we work in most:</p>
          <div className="flex flex-wrap justify-center gap-x-2 gap-y-2">
            {SERVICE_AREAS.map(area => (
              <a
                key={area.slug}
                href={`/tree-service/${area.slug}`}
                className="text-green-700 hover:text-green-800 underline decoration-green-200 hover:decoration-green-500 underline-offset-4 px-1"
              >
                {area.name}
              </a>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-4">
            Plus Crofton, Clarksville, Odenton, Owings Mills, Sykesville and nearby communities.
          </p>
        </div>
      </section>

      {/* Trust Image Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg"
                  alt="Professional tree service team working safely"
                  loading="lazy"
                  className="w-full h-64 sm:h-80 object-cover"
                />
              </div>
              
              {/* Simple trust badge - mobile friendly */}
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-gray-900">Licensed & Insured</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-4">
                WHY CHOOSE US
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6">
                Professional Tree Care You Can Trust
              </h2>
              <p className="text-base lg:text-lg text-gray-600 mb-6 leading-relaxed">
                When you call One Choice Tree Service, you're getting more than just tree removal. You're getting peace of mind from a team that treats your property like our own.
              </p>
              
              {/* Trust points - mobile optimized */}
              <div className="space-y-3 lg:space-y-4">
                {[
                  { icon: CheckCircle, text: "Every crew member is trained and certified" },
                  { icon: Shield, text: "Comprehensive insurance protects your property" },
                  { icon: Star, text: `${reviewData.rating.toFixed(1)}/5 on Google from ${reviewData.count} reviews` },
                  { icon: Phone, text: "24/7 emergency response when you need it" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-3 h-3 lg:w-4 lg:h-4 text-green-600" />
                    </div>
                    <span className="text-sm lg:text-base text-gray-700 font-medium leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about our tree services.
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{item.question}</span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {expandedFAQ === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Don't wait. Your tree problem won't solve itself.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="bg-gradient-to-r from-white to-emerald-50 hover:from-emerald-50 hover:to-emerald-100 text-green-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 hover:scale-105"
              onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Get a Free Quote ↑
            </button>
            <a
              href="tel:+14432080747"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
            >
              Call (443) 208-0747
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      {/* Add the QuoteModal component */}
      <QuoteModal />
    </div>
  );
};

export default ContactPage;